// The one skeleton every side panel is built from (Gennemgang, Compliance incl. Microsoft Secure Score, Konfiguration):
// header · one status line · tabs.
//   Overblik  = the three descriptions (Hvad er det · Situationen hos jer · Anbefaling) in one frame, and right
//               below them the Dokumentation form, so the person can decide on the spot.
//   Evidens   = the raw facts, and nothing else.
//   (third)   = the object's own tab when it has one (Medlemskab, Politikker, Kontroller, Resultat ...).
// Beslutninger keeps its own design and does not use this.
//
// One type scale throughout (see DetailPanel.css): 10 label · 11 pill · 12 small · 13 body/tabs · 14 status · 17 title.
// Rules: prose stands free on the panel, settings (label + value) stand in a framed block (DataBlock); a row that
// opens something is itself the button (never a repeated "Åbn …" text link); a long list sits behind a fold
// (Fold) or a symbol (MoreToggle), never a "Vis N flere" text.
import React, { useState } from 'react';
import NcIcon from './NcIcon';
import './DetailPanel.css';

export const PANEL_TABS = [['overblik', 'Overblik'], ['evidens', 'Evidens']];

const Chev = ({ open }) => (
  <svg className={`dp-chev${open ? ' is-open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
);

// pill: { tone: 'bad' | 'warn' | 'ok' | 'handled' | 'neutral', text }
export function PanelHead({ icon, title, sub, pill }) {
  return (
    <div className="detail-head detail-head--compact">
      <div className="detail-head__id">
        <div className="od-avatar" aria-hidden="true"><NcIcon name={icon || 'grid'} size={18} /></div>
        <div className="detail-head__text">
          <h3 className="detail-head__title" title={title}>{title}</h3>
          {sub && <div className="detail-head__sub">{sub}</div>}
        </div>
      </div>
      {pill && <span className={`dp-pill dp-pill--${pill.tone || 'neutral'}`}>{pill.text}</span>}
    </div>
  );
}

// One line saying what is the case, coloured by its tone. Nothing else repeats it below.
export function PanelStatus({ tone, children }) {
  if (!children) return null;
  return <div className={`dp-status dp-status--${tone || 'neutral'}`}><p>{children}</p></div>;
}

// extra = [key, label] of the object's own tab (Medlemskab, Politikker, Kontroller, Resultat ...), kept as its own tab.
export function PanelTabs({ active, onChange, extra }) {
  return (
    <div className="detail-tabs">
      {[...PANEL_TABS, ...(extra ? [extra] : [])].map(([key, label]) => (
        <button key={key} type="button" className={`detail-tab${active === key ? ' is-active' : ''}`} onClick={() => onChange(key)}>{label}</button>
      ))}
    </div>
  );
}

export function PanelLabel({ children, right }) {
  return <div className="dp-lab dp-lab--row"><span>{children}</span>{right}</div>;
}

// The three descriptions, in one frame. what/situation may be left out when there is nothing true to say; the
// recommendation is always there, so the shape never changes ("Ingen handling nødvendig." when there is none).
// tone: the object's own tone ('bad' makes the recommendation red, anything else with a recommendation is orange).
// The frame is a white card with a soft shadow, lifted off the panel (the recommendation keeps its own colour).
export function Trio({ what, situation, recommend, tone, noneText = 'Ingen handling nødvendig.', recommendLabel = 'Anbefaling' }) {
  const recTone = recommend ? (tone === 'bad' ? 'bad' : 'warn') : 'none';
  return (
    <div className="dp-trio">
      {what && <div className="dp-sec"><div className="dp-lab">Hvad er det</div><p className="dp-tx">{what}</p></div>}
      {situation && (
        <div className="dp-sec">
          <div className="dp-lab">Situationen hos jer</div>
          {typeof situation === 'string' ? <p className="dp-tx">{situation}</p> : situation}
        </div>
      )}
      <div className={`dp-sec dp-sec--${recTone}`}>
        <div className="dp-lab">{recommendLabel}</div>
        <p className="dp-tx">{recommend || noneText}</p>
      </div>
    </div>
  );
}

// Dokumentation sits right under the three descriptions on Overblik. children = the form.
// It is NordComply's main product, so it stands apart: a white card lifted off the panel, with a title row, then the form.
export function DocSection({ children }) {
  return (
    <div className="dp-doc">
      <div className="dp-doc__hd">
        <span className="dp-doc__ic" aria-hidden="true"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z" /><path d="M9 12l2 2 4-4" /></svg></span>
        <h4>Dokumentation</h4>
        <p>Din beslutning gemmes på objektet og samles under Beslutninger.</p>
      </div>
      <div className="dp-doc__in">{children}</div>
    </div>
  );
}

// Settings and values: label left, value right, in a frame with no fill.
// rows: [label, value, mono?, flag?]  flag: true = orange and bold, 'gap' = red (a value we do not have yet)
export function DataBlock({ rows }) {
  const shown = (rows || []).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (shown.length === 0) return null;
  return (
    <div className="dp-data">
      {shown.map(([k, v, mono, warn]) => (
        <div className="dp-data__r" key={k}>
          <span>{k}</span>
          <span className={`${mono ? 'is-mono' : ''}${warn === 'gap' ? ' dp-gap' : warn ? ' is-w' : ''}`}>{v}</span>
        </div>
      ))}
    </div>
  );
}

// A group inside a side-panel card: a quiet heading with its count, then the rows, always shown (no box, nothing to open first).
// limit: how many rows show at first; the rest come with the chevron.
export function Group({ label, count, tone, limit, children }) {
  const [shown, setShown] = useState(limit || Infinity);
  const kids = React.Children.toArray(children);
  return (
    <div className={`dp-grp${tone ? ` dp-grp--${tone}` : ''}`}>
      <div className="dp-grp__h">{label}{count !== undefined && <em>{count}</em>}</div>
      {kids.slice(0, shown)}
      {kids.length > shown && <MoreToggle open={false} hiddenCount={kids.length - shown} onClick={() => setShown((n) => n + 30)} />}
    </div>
  );
}

// A list behind a fold, e.g. "Skal rettes (17)" and "Opfyldt (71)". hint shows only while open.
// tone colours the label and count like the status it stands for (bad = red, ok = green, warn = orange).
export function Fold({ label, count, hint, tone, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`dp-fold${open ? ' is-open' : ''}${tone ? ` dp-fold--${tone}` : ''}`}>
      <button type="button" className="dp-fold__h" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="dp-fold__t">{label}{count !== undefined && <em>{count}</em>}</span>
        {open && hint && <span className="dp-fold__hint">{hint}</span>}
        <Chev open={open} />
      </button>
      {open && <div className="dp-fold__b">{children}</div>}
    </div>
  );
}

// A short, quiet "show more / less" in place of a text link: a small round chevron.
export function MoreToggle({ open, onClick, hiddenCount }) {
  const label = open ? 'Vis færre' : hiddenCount ? `Vis ${hiddenCount} flere` : 'Vis flere';
  return (
    <button type="button" className="dp-more" onClick={onClick} aria-label={label} aria-expanded={!!open} title={label}>
      <Chev open={open} />
    </button>
  );
}

// One object in a list. The whole row is the button; the chevron says it opens something.
// pill: { tone, text } on the right, sub: a quiet second line.
export function RowButton({ title, sub, pill, onClick }) {
  const inner = (
    <>
      <span className="dp-row__t"><b>{title}</b>{sub && <small>{sub}</small>}</span>
      {pill && <span className={`dp-pill dp-pill--${pill.tone || 'neutral'}`}>{pill.text}</span>}
      {onClick && <svg className="dp-row__go" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>}
    </>
  );
  return onClick
    ? <button type="button" className="dp-row" onClick={onClick}>{inner}</button>
    : <div className="dp-row dp-row--static">{inner}</div>;
}
